import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, type Firestore } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator, type Functions } from "firebase/functions";
import { getAnalytics, type Analytics } from "firebase/analytics";
import { getPerformance, type FirebasePerformance } from "firebase/performance";

const REQUIRED_ENV_KEYS = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_APP_ID",
] as const;

/** DEV 환경에서 .env 없이도 emulator 연결 가능하도록 fallback 기본값 */
const DEV_DEFAULTS: Record<string, string> = {
  VITE_FIREBASE_API_KEY: "demo-api-key",
  VITE_FIREBASE_AUTH_DOMAIN: "demo.firebaseapp.com",
  VITE_FIREBASE_PROJECT_ID: "demo-project",
  VITE_FIREBASE_STORAGE_BUCKET: "demo.appspot.com",
  VITE_FIREBASE_MESSAGING_SENDER_ID: "123456789",
  VITE_FIREBASE_APP_ID: "1:123456789:web:abc123",
};

function getEnvVar(key: string): string {
  return import.meta.env[key] || (import.meta.env.DEV ? (DEV_DEFAULTS[key] ?? "") : "");
}

let _envValid: boolean | null = null;
let _envWarned = false;

function isEnvValid(): boolean {
  if (_envValid !== null) return _envValid;

  // DEV 환경에서는 fallback 기본값이 있으므로 항상 유효
  if (import.meta.env.DEV) {
    const missing = REQUIRED_ENV_KEYS.filter((key) => !import.meta.env[key]);
    if (missing.length > 0 && !_envWarned) {
      _envWarned = true;
      console.warn(
        `⚠️ [Firebase] .env 미설정 → DEV 기본값으로 Firebase Emulator에 연결합니다.\n` +
          `  누락된 변수: ${missing.join(", ")}`,
      );
    }
    _envValid = true;
    return true;
  }

  // Production에서는 환경변수 필수
  const missing = REQUIRED_ENV_KEYS.filter((key) => !import.meta.env[key]);
  _envValid = missing.length === 0;

  if (!_envValid) {
    throw new Error(`Missing required environment variables: ${missing.join(", ")}`);
  }

  return _envValid;
}

/** Firebase가 정상 초기화되었는지 여부 */
export function isFirebaseAvailable(): boolean {
  return isEnvValid();
}

function createFirebaseApp(): FirebaseApp {
  return initializeApp({
    apiKey: getEnvVar("VITE_FIREBASE_API_KEY"),
    authDomain: getEnvVar("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: getEnvVar("VITE_FIREBASE_PROJECT_ID"),
    storageBucket: getEnvVar("VITE_FIREBASE_STORAGE_BUCKET"),
    messagingSenderId: getEnvVar("VITE_FIREBASE_MESSAGING_SENDER_ID"),
    appId: getEnvVar("VITE_FIREBASE_APP_ID"),
    measurementId: getEnvVar("VITE_FIREBASE_MEASUREMENT_ID"),
  });
}

/** 에뮬레이터 모드 여부 (Analytics/Performance는 에뮬레이터에서 사용 불가) */
function isEmulatorMode(): boolean {
  // 프로덕션 빌드에서는 절대 에뮬레이터 사용 불가
  if (import.meta.env.PROD) return false;

  if (import.meta.env.VITE_USE_EMULATOR) {
    console.warn(
      "⚠️ [Firebase] VITE_USE_EMULATOR는 무시됩니다. VITE_USE_FIREBASE_EMULATOR를 사용하세요.",
    );
  }
  return (
    import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true" ||
    (import.meta.env.DEV && !import.meta.env.VITE_FIREBASE_API_KEY)
  );
}

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let functions: Functions | null = null;
let analyticsInstance: Analytics | null = null;
let performanceInstance: FirebasePerformance | null = null;

function ensureInitialized(): void {
  if (app || !isEnvValid()) return;

  app = createFirebaseApp();

  auth = getAuth(app);
  const databaseId = getEnvVar("VITE_FIRESTORE_DATABASE_ID");
  db = databaseId ? getFirestore(app, databaseId) : getFirestore(app);
  functions = getFunctions(app);

  const useEmulator = isEmulatorMode();

  if (import.meta.env.DEV && !useEmulator) {
    console.warn(
      "⚠️ [Firebase] DEV 모드에서 프로덕션 Firebase에 연결됩니다!\n" +
        "  에뮬레이터를 사용하려면 .env에 VITE_USE_FIREBASE_EMULATOR=true를 설정하세요.\n" +
        "  의도적이라면 이 경고를 무시하세요.",
    );
  }

  if (useEmulator) {
    connectAuthEmulator(auth, "http://127.0.0.1:9299", {
      disableWarnings: true,
    });
    connectFirestoreEmulator(db, "127.0.0.1", 8280);
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  }
}

export function getFirebaseAuth(): Auth {
  ensureInitialized();
  if (!auth) throw new Error("Firebase Auth not initialized. Check .env configuration.");
  return auth;
}

export function getFirebaseFirestore(): Firestore {
  ensureInitialized();
  if (!db) throw new Error("Firebase Firestore not initialized. Check .env configuration.");
  return db;
}

export function getFirebaseFunctions(): Functions {
  ensureInitialized();
  if (!functions) throw new Error("Firebase Functions not initialized. Check .env configuration.");
  return functions;
}

export function getFirebaseApp(): FirebaseApp {
  ensureInitialized();
  if (!app) throw new Error("Firebase App not initialized. Check .env configuration.");
  return app;
}

/**
 * Firebase Analytics 인스턴스를 반환한다.
 * 에뮬레이터 모드이거나 measurementId가 없으면 null을 반환한다.
 */
export function getFirebaseAnalytics(): Analytics | null {
  ensureInitialized();
  if (!analyticsInstance && app && !isEmulatorMode() && getEnvVar("VITE_FIREBASE_MEASUREMENT_ID")) {
    analyticsInstance = getAnalytics(app);
  }
  return analyticsInstance;
}

/**
 * Firebase Performance 인스턴스를 반환한다.
 * 에뮬레이터 모드이면 null을 반환한다.
 */
export function getFirebasePerformance(): FirebasePerformance | null {
  ensureInitialized();
  if (!performanceInstance && app && !isEmulatorMode()) {
    performanceInstance = getPerformance(app);
  }
  return performanceInstance;
}
